import { Box, Stack, styled, Typography } from '@mui/material'
import { Outlet, Route, Routes } from 'react-router-dom'
import Account from './Account'
import SubpageTabs, { SubpageTabLink } from 'components/SubpageTabs'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import EmailSettings from './EmailSettings'
import SlackSettings from './SlackSettings'
import Billing from './Billing'
import PersonalSpace from './PersonalSpace'
import { useFetchActiveOrganization } from 'hooks/use-organization'
import { OrganizationKind, SubscriptionStatus } from 'api/model'
import Company from './Company'
import { COLORS } from 'app-constants'
import DataAndPrivacy from './DataAndPrivacy'
import { authenticatedUserAtom } from 'state'
import { useRecoilValue } from 'recoil'
import AcquisitionBanner from 'components/AcquisitionBanner/AcquisitionBanner'

export default function Organization() {
  const user = useRecoilValue(authenticatedUserAtom)
  const hasAddedSlackApp = user?.has_slack
  // if the trial has ended, then the trial_end should be before today's date
  // we should only display the billing tab once the ttrial has ended
  const hasTrialEnded = user?.subscription?.trial_end && new Date(user.subscription.trial_end) < new Date()

  return (
    <Routes>
      <Route element={<OrganizationContainer />}>
        <Route index element={<Account />} />
        <Route path="/personal-space" element={<PersonalSpace />} />
        <Route path="/company" element={<Company />} />
        <Route path="/emails" element={<EmailSettings />} />
        {hasAddedSlackApp && <Route path="/slack" element={<SlackSettings />} />}
        <Route path="/data-and-privacy" element={<DataAndPrivacy />} />
        {hasTrialEnded && <Route path="/billing" element={<Billing />} />}
      </Route>
    </Routes>
  )
}

const MainBox = styled(Box)`
  width: 100%;
`

const OrganizationContainer = () => {
  const { organization } = useFetchActiveOrganization()
  const organizationKind = organization?.organization_kind
  const isMobile = useCheckMobileScreen()
  const user = useRecoilValue(authenticatedUserAtom)
  const hasAddedSlackApp = user?.has_slack
  const isSubscriptionCancelled = user?.subscription?.status === SubscriptionStatus.canceled

  // determine if today date is past the trial end date on the user.subscription.trial_end_date
  const hasTrialEnded = user?.subscription?.trial_end && new Date(user.subscription.trial_end) < new Date()

  if (isMobile) {
    return (
      <MainBox>
        <Outlet />
      </MainBox>
    )
  }

  return (
    <Stack
      sx={{
        paddingLeft: isMobile ? '16px' : 0,
      }}>
      <Stack direction="row" spacing={isMobile ? 2 : 0} justifyContent="flex-start">
        <Stack
          sx={{
            p: 3,
            background: COLORS.PURE_WHITE,
            height: '100vh',
          }}>
          <Typography variant="h4" mb={6}>
            Settings
          </Typography>
          <SubpageTabs>
            <SubpageTabLink to="/organization" label="Account" />
            {organizationKind === OrganizationKind.personal && !isSubscriptionCancelled && (
              <SubpageTabLink to="/organization/personal-space" label="Personal space" />
            )}
            {organizationKind === OrganizationKind.company && !isSubscriptionCancelled && (
              <SubpageTabLink to="/organization/company" label="Company" />
            )}
            {!isSubscriptionCancelled && <SubpageTabLink to="/organization/emails" label="Emails" />}
            {!isSubscriptionCancelled && hasAddedSlackApp && <SubpageTabLink to="/organization/slack" label="Slack" />}
            {!isSubscriptionCancelled && <SubpageTabLink to="/organization/data-and-privacy" label="Data & Privacy" />}
            {hasTrialEnded && <SubpageTabLink to="/organization/billing" label="Billing" />}
          </SubpageTabs>
        </Stack>

        <MainBox
          style={{
            height: '100vh',
            overflowY: 'scroll',
          }}>
          <AcquisitionBanner />
          <Outlet />
        </MainBox>
      </Stack>
    </Stack>
  )
}
